import { Fragment, FunctionComponent } from 'react';
import { useSelector } from 'react-redux';
import { trackFeature } from '@/utils/adobeLaunch';
import { t } from '@/utils/i18n';
import { isEmbroideryMode } from '@/utils/params';
import { getIsOrganic } from '@/model/productType/getIsOrganic';
import { getIsScreenPrintingAppearance } from '@/model/productType/getIsScreenPrintingAppearance';
import { getIsStickerAppearance } from '@/model/productType/getIsStickerAppearance';
import { getCurrentAppearance } from '@/redux/draft/getters/getCurrentAppearance';
import { getProductType } from '@/redux/draft/getters/getProductType';
import { getPrintTypeIdsForDeliveryETA } from '@/redux/printTypes/getters/getPrintTypeIdsForDeliveryETA';
import { Badge, BadgeTypes } from '@/components/common/Badge';
import { DeliveryEta } from '@/components/common/DeliveryEta';
import { PrintTypeSummary } from '@/components/common/PrintTypeSummary';
import { ProductTypeRating } from '@/components/common/ProductTypeRating';
import { TextRouterLink } from '@/components/common/TextLink';
import { CaretRight } from '@/components/common/icons/Caret';
import { Leaf } from '@/components/common/icons/Leaf';
import './ProductTypeInfo.scss';

export const ProductTypeInfo: FunctionComponent = () => {
  const productType = useSelector(getProductType);

  const currentAppearance = useSelector(getCurrentAppearance);
  const printTypeIds = useSelector(getPrintTypeIdsForDeliveryETA);

  const handleProductDetailsClick = () => {
    trackFeature('product-details');
  };

  const isScreenPrintingAppearance = getIsScreenPrintingAppearance(currentAppearance);
  const isOrganic = getIsOrganic(productType);

  return (
    <div className="product-type-info">
      <h3 className="product-type-info__headline">{productType.name}</h3>
      {(isOrganic || isScreenPrintingAppearance || isEmbroideryMode()) && (
        <div className="product-type-info__badges">
          {isOrganic && (
            <Badge type={BadgeTypes.Organic} title={t('sustainable.label')}>
              <Leaf width="1rem" height="1rem" />
            </Badge>
          )}
          {isEmbroideryMode() && currentAppearance.printTypes.length > 0 && (
            <Badge type={BadgeTypes.PrintType}>{t('embroidery.badge.text')}</Badge>
          )}
          {isScreenPrintingAppearance && (
            <Fragment>
              <Badge type={BadgeTypes.MinAmount}>{t('productDetails.screenprintingBadge')}</Badge>
              <span className="product-type-info__min-items">{t('productDetails.minItems')}</span>
            </Fragment>
          )}
        </div>
      )}
      <DeliveryEta
        productTypeId={productType.id}
        printTypeIds={printTypeIds}
        className="product-type-info__delivery-eta"
      />
      <ProductTypeRating productType={productType} className="product-type-info__line" />
      <TextRouterLink
        to="/product-details"
        onClick={handleProductDetailsClick}
        className="product-type-info__detail-link"
      >
        {t('productInfoPanel.title')}
        <CaretRight className="product-type-info__detail-icon" />
      </TextRouterLink>
      {!isScreenPrintingAppearance && <PrintTypeSummary className="product-type-info__line" />}
      <div className="product-type-info__line">
        <strong>
          {t(
            getIsStickerAppearance(currentAppearance)
              ? 'sticker.finish'
              : 'productInfoPanel.productColor'
          )}
          :&nbsp;
        </strong>
        {currentAppearance.name}
      </div>
    </div>
  );
};
